import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { chartColor, DONUT_COLOR_COUNT, donutOtherColor, donutSegmentColor } from '../../shared/components/charts/chart-palette';
import { ChartPointModel }                                                   from '../../shared/components/charts/typings/chart-point';
import { ChartSeriesModel }                                                  from '../../shared/components/charts/typings/chart-series';
import { DonutSegmentModel }                                                 from '../../shared/components/charts/typings/donut-segment';
import { IntelligenceFinancialFiltersModel }                                 from '../typings/intelligence.financial-filters';
import { IntelligenceFinancialsModel }                                       from '../typings/intelligence.financials';
import { IntelligenceFormatService }                                         from '../intelligence.format.service';
import { IntelligenceService }                                               from '../intelligence.service';

@Component({
  selector   : 'wor-intelligence-money-tab',
  template: require('./intelligence.money-tab.component.html')
})
export class MoneyTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public agingRows      : Array<{ invoice_count : number; key : string; outstanding : number }> = [];
  public payablesRows   : Array<{ invoice_count : number; key : string; outstanding : number }> = [];
  public methodSegments : Array<DonutSegmentModel> = [];
  public mixPoints      : Array<ChartPointModel> = [];
  public paymentsPoints : Array<ChartPointModel> = [];
  public revenuePoints  : Array<ChartPointModel> = [];
  public sourceSegments : Array<DonutSegmentModel> = [];

  public filterOptions : { payment_methods : Array<string>; revenue_sources : Array<string> } = null;
  public filters       : IntelligenceFinancialFiltersModel = { payment_method: '', revenue_source: '' };

  public mixSeries : Array<ChartSeriesModel> = [
    { color: chartColor(2), key: 'labour_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.LABOUR') },
    { color: chartColor(1), key: 'parts_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.PARTS') },
    { color: chartColor(3), key: 'sublet_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.SUBLET') },
    { color: chartColor(4), key: 'other_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER') }
  ];

  public paymentsSeries : Array<ChartSeriesModel> = [
    { color: chartColor(1), key: 'payments', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.PAYMENTS') }
  ];

  public revenueSeries : Array<ChartSeriesModel> = [
    { color: chartColor(1), key: 'revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.REVENUE') },
    { color: chartColor(0), key: 'cost', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.COST') },
    { color: chartColor(2), key: 'gross_profit', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.GROSS_PROFIT') }
  ];

  private loadGeneration : number = 0;
  private methodAmounts  : { [ method : string ] : number } = {};
  private sourceAmounts  : { [ source : string ] : number } = {};
  private subscription   : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  // These filters narrow client side, so clearing them is instant - no reload.
  clearFilters () : void {
    this.filters = { payment_method: '', revenue_source: '' };

    this.buildSegments();
  }

  // the donut's own filter outranks the day drill on the same card
  clearSegment ( field : keyof IntelligenceFinancialFiltersModel ) : void {
    if ( this.filters[ field ] ) {
      this.setFilter(field, '');

      return;
    }

    this.restoreDay();
  }

  hasActiveFilters () : boolean {
    return !!this.filters.payment_method || !!this.filters.revenue_source;
  }

  dayLabel () : string {
    return this.intelligenceService.previousRange
      ? this.intelligenceFormatService.date(this.intelligenceService.range.start_date)
      : '';
  }

  isAgingEmpty () : boolean {
    return !this.agingRows.some(row => row.outstanding > 0);
  }

  isMixEmpty () : boolean {
    return this.pointsEmpty(this.mixPoints);
  }

  isPayablesEmpty () : boolean {
    return !this.payablesRows.some(row => row.outstanding > 0);
  }

  isMethodsEmpty () : boolean {
    return !this.methodSegments.some(segment => segment.value > 0);
  }

  isPaymentsEmpty () : boolean {
    return this.pointsEmpty(this.paymentsPoints);
  }

  isSourcesEmpty () : boolean {
    return !this.sourceSegments.some(segment => segment.value > 0);
  }

  isRevenueEmpty () : boolean {
    return this.pointsEmpty(this.revenuePoints);
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getFinancials()
    .then(( data : IntelligenceFinancialsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.revenuePoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          cost        : day.cost,
          gross_profit: day.gross_profit,
          revenue     : day.revenue
        }
      }));

      this.paymentsPoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: { payments: day.payments }
      }));

      this.mixPoints = ( data.sales_mix_series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          labour_revenue: day.labour_revenue,
          other_revenue : day.other_revenue,
          parts_revenue : day.parts_revenue,
          sublet_revenue: day.sublet_revenue
        }
      }));

      this.agingRows = this.buildAgingRows(data.receivables_aging || {});
      this.payablesRows = this.buildAgingRows(data.payables_aging || {});

      this.sourceAmounts = data.revenue_by_source || {};
      this.methodAmounts = data.payments_by_method || {};

      // rebuilt from the unfiltered totals on each load, so choosing one never collapses the list
      this.filterOptions = {
        payment_methods: Object.keys(this.methodAmounts).sort(),
        revenue_sources: Object.keys(this.sourceAmounts).sort()
      };

      this.dropFiltersMissingFromOptions();
      this.buildSegments();
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  paymentsTotal () : number {
    return this.sum(this.visible(this.methodAmounts, this.filters.payment_method));
  }

  restoreDay () : void {
    this.intelligenceService.restoreRange();
  }

  revenueTotal () : number {
    return this.sum(this.visible(this.sourceAmounts, this.filters.revenue_source));
  }

  selectDay ( point : ChartPointModel ) : void {
    this.intelligenceService.selectDay(point.key);
  }

  setFilter ( field : keyof IntelligenceFinancialFiltersModel, value : string ) : void {
    this.filters[ field ] = value;

    this.buildSegments();
  }

  toggleFilter ( field : keyof IntelligenceFinancialFiltersModel, value : string ) : void {
    this.setFilter(field, this.filters[ field ] === value
      ? ''
      : value);
  }

  topMethodLabel () : string {
    return this.topLabel(this.visible(this.methodAmounts, this.filters.payment_method));
  }

  topMethodShare () : string {
    return this.share(this.visible(this.methodAmounts, this.filters.payment_method), this.methodAmounts);
  }

  topSourceLabel () : string {
    return this.topLabel(this.visible(this.sourceAmounts, this.filters.revenue_source));
  }

  topSourceShare () : string {
    return this.share(this.visible(this.sourceAmounts, this.filters.revenue_source), this.sourceAmounts);
  }

  // the endpoint returns the full split, so narrowing here skips a slow query
  private buildSegments () : void {
    this.methodSegments = this.buildSourceSegments(this.visible(this.methodAmounts, this.filters.payment_method));
    this.sourceSegments = this.buildSourceSegments(this.visible(this.sourceAmounts, this.filters.revenue_source));
  }

  // drops filters the period no longer offers; no reload, as these lists are never narrowed
  private dropFiltersMissingFromOptions () : void {
    const offered = ( value : string, options : Array<string> ) : string => value && options.indexOf(value) !== -1
      ? value
      : '';

    this.filters = {
      payment_method: offered(this.filters.payment_method, this.filterOptions.payment_methods),
      revenue_source: offered(this.filters.revenue_source, this.filterOptions.revenue_sources)
    };
  }

  private share ( amounts : { [ key : string ] : number }, against : { [ key : string ] : number } ) : string {
    const total = this.sum(against);

    return this.intelligenceFormatService.percent(total
      ? ( this.topAmount(amounts) / total ) * 100
      : 0);
  }

  private sum ( amounts : { [ key : string ] : number } ) : number {
    return Object.keys(amounts).reduce(( running, key ) => running + ( amounts[ key ] || 0 ), 0);
  }

  private topAmount ( amounts : { [ key : string ] : number } ) : number {
    return Object.keys(amounts).reduce(( highest, key ) => Math.max(highest, amounts[ key ] || 0), 0);
  }

  private topLabel ( amounts : { [ key : string ] : number } ) : string {
    return Object.keys(amounts).reduce(( best, key ) => !best || ( amounts[ key ] || 0 ) > ( amounts[ best ] || 0 )
      ? key
      : best, '');
  }

  private visible ( amounts : { [ key : string ] : number }, selected : string ) : { [ key : string ] : number } {
    if ( !selected || amounts[ selected ] === undefined ) {
      return amounts;
    }

    return { [ selected ]: amounts[ selected ] };
  }

  // Sources ranked by revenue; anything past the palette folds into Other.
  private buildSourceSegments ( amounts : { [ source : string ] : number } ) : Array<DonutSegmentModel> {
    const entries = Object.keys(amounts)
      .map(label => ({ amount: amounts[ label ] || 0, label }))
      .sort(( first, second ) => second.amount - first.amount);

    const kept = entries.slice(0, DONUT_COLOR_COUNT);
    const remainder = entries.slice(DONUT_COLOR_COUNT).reduce(( sum, entry ) => sum + entry.amount, 0);

    const segments : Array<DonutSegmentModel> = kept.map(( entry, index ) => ({
      color      : donutSegmentColor(index),
      isDrillable: true,
      key        : entry.label,
      label      : entry.label,
      value      : entry.amount
    }));

    if ( remainder > 0 ) {
      segments.push({
        color : donutOtherColor(),
        label : this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
        others: entries.slice(DONUT_COLOR_COUNT).map(( entry, index ) => ({
          color      : donutSegmentColor(index),
          isDrillable: true,
          key        : entry.label,
          label      : entry.label,
          value      : entry.amount
        })),
        value : remainder
      });
    }

    return segments;
  }

  private buildAgingRows ( aging : { [ bucket : string ] : { invoice_count : number; outstanding : number } } ) : Array<{ invoice_count : number; key : string; outstanding : number }> {
    return [ 'current', 'days_31_60', 'days_61_90', 'days_90_plus' ].map(key => ({
      invoice_count: aging[ key ] ? aging[ key ].invoice_count : 0,
      key,
      outstanding  : aging[ key ] ? aging[ key ].outstanding : 0
    }));
  }

  private pointsEmpty ( points : Array<ChartPointModel> ) : boolean {
    return !points.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }
}
